// Crop dialog for avatars, banners and server icons. Drag to move, slider / wheel / +/- keys to zoom, arrow keys to nudge.
// The server does the actual crop on the original file (so animated GIF/WebP stay animated); we only send the rectangle.
import { useEffect, useRef, useState } from "react";
type RPointerEvent = { clientX: number; clientY: number; pointerId: number; target: EventTarget };
import { t } from "../lib/i18n.ts";
import { Modal } from "./common.tsx";

export interface CropRect { x: number; y: number; w: number; h: number }
export const ACCEPT_IMAGES = "image/png,image/jpeg,image/gif,image/webp";
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export function ImageCrop({ file, aspect, round, onCancel, onSave }: {
  file: File; aspect: number; round?: boolean; onCancel: () => void; onSave: (crop: CropRect) => Promise<void>;
}) {
  const VW = aspect >= 1 ? 360 : Math.round(360 * aspect), VH = Math.round(VW / aspect);
  const [url] = useState(() => URL.createObjectURL(file));
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const drag = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const base = nat ? Math.max(VW / nat.w, VH / nat.h) : 1;
  const scale = base * zoom;
  const dw = nat ? nat.w * scale : VW, dh = nat ? nat.h * scale : VH;
  const clamp = (o: { x: number; y: number }, s = scale) => {
    const w = (nat?.w ?? 0) * s, h = (nat?.h ?? 0) * s;
    return { x: Math.min(0, Math.max(VW - w, o.x)), y: Math.min(0, Math.max(VH - h, o.y)) };
  };
  const zoomTo = (z: number) => {
    const nz = Math.min(4, Math.max(1, z)), ns = base * nz;
    // keep the viewport centre fixed while zooming
    const cx = (VW / 2 - off.x) / scale, cy = (VH / 2 - off.y) / scale;
    setZoom(nz); setOff(clamp({ x: VW / 2 - cx * ns, y: VH / 2 - cy * ns }, ns));
  };
  const onDown = (e: RPointerEvent) => { (e.target as Element).setPointerCapture(e.pointerId); drag.current = { px: e.clientX, py: e.clientY, ox: off.x, oy: off.y }; };
  const onMove = (e: RPointerEvent) => { const d = drag.current; if (d) setOff(clamp({ x: d.ox + e.clientX - d.px, y: d.oy + e.clientY - d.py })); };
  const rect = (): CropRect => {
    const n = nat!;
    const w = Math.min(n.w, Math.round(VW / scale)), h = Math.min(n.h, Math.round(VH / scale));
    return { x: Math.max(0, Math.min(n.w - w, Math.round(-off.x / scale))), y: Math.max(0, Math.min(n.h - h, Math.round(-off.y / scale))), w, h };
  };
  const save = async () => { setBusy(true); setErr(null); try { await onSave(rect()); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } };

  return (
    <Modal title={t("cropImage")} onClose={onCancel} footer={<>
      <button className="btn ghost" onClick={onCancel}>{t("cancel")}</button>
      <button className="btn pri" id="crop-save" disabled={!nat || busy} onClick={save}>{busy ? t("uploading") : t("apply")}</button></>}>
      <div className="crop-wrap" dir="ltr">
        <div className={`crop${round ? " round" : ""}`} style={{ width: VW, height: VH }} tabIndex={0} aria-label={t("cropHint")}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
          onWheel={(e) => zoomTo(zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08))}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 20 : 5;
            if (e.key === "ArrowLeft") setOff(clamp({ ...off, x: off.x + step })); else if (e.key === "ArrowRight") setOff(clamp({ ...off, x: off.x - step }));
            else if (e.key === "ArrowUp") setOff(clamp({ ...off, y: off.y + step })); else if (e.key === "ArrowDown") setOff(clamp({ ...off, y: off.y - step }));
            else if (e.key === "+" || e.key === "=") zoomTo(zoom * 1.1); else if (e.key === "-") zoomTo(zoom / 1.1); else return;
            e.preventDefault();
          }}>
          <img src={url} alt="" draggable={false} style={{ width: dw, height: dh, transform: `translate(${off.x}px, ${off.y}px)` }}
            onLoad={(e) => { const i = e.currentTarget; const n = { w: i.naturalWidth, h: i.naturalHeight }; setNat(n);
              const s = Math.max(VW / n.w, VH / n.h); setOff({ x: (VW - n.w * s) / 2, y: (VH - n.h * s) / 2 }); }}
            onError={() => setErr(t("invalidImage"))} />
        </div>
        <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => zoomTo(Number(e.target.value))} aria-label={t("zoom")} style={{ width: VW }} />
        <small className="muted">{t("cropHint")}{file.type === "image/gif" || file.type === "image/webp" ? ` ${t("animatedKept")}` : ""}</small>
      </div>
      {err && <p className="err" role="alert">{err}</p>}
    </Modal>
  );
}

/** Hidden file input + validation; returns a picker to open and the chosen file. */
export function pickImage(onFile: (f: File) => void, onError: (msg: string) => void) {
  const input = document.createElement("input");
  input.type = "file"; input.accept = ACCEPT_IMAGES;
  input.onchange = () => {
    const f = input.files?.[0]; if (!f) return;
    if (!ACCEPT_IMAGES.split(",").includes(f.type)) { onError(t("invalidImage")); return; }
    if (f.size > MAX_IMAGE_BYTES) { onError(t("imageTooLarge")); return; }
    onFile(f);
  };
  input.click();
}
